import {Injectable, OnDestroy} from '@angular/core';
import {ConfigPermission} from '@docma-mm-core/permission/config-permission';
import {ProjectActionPermissionEnum} from '@docma-mm-core/permission/project-action-permission.enum';
import {ProjectUserPermissions} from '@docma-mm-core/permission/project-user-permissions';
import {Project as MmProject} from '@docma-mm-core/project/project';
import {ConnectivityService} from '@mydocma/core';
import {BehaviorSubject, combineLatest, Observable, of} from 'rxjs';
import {filter, first, map, switchMap, tap, distinctUntilChanged, shareReplay} from 'rxjs/operators';
import {MmProjectOfflineService} from './mm-project-offline.service';
import {MmProjectService} from './mm-project.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {ViewPoint} from '@docma-mm-core/status/viewpoint.enum';
import {MmProjectLoaderService} from './mm-project-loader.service';
import {ActionPermissionEnum} from '@docma-mm-core/permission/action-permission.enum';
import {ConfigPermissionEnum} from '@docma-mm-core/permission/config-permission.enum';
import {DataAccessPermissionEnum} from '@docma-mm-core/permission/data-access-permission.enum';
import {MmRoutesEnum} from '../mm-routing/mm-routes.enum';
import {routeToPath} from '../mm-routing/mm-routes.map';
import {CoPermissionArea, CoPermissionsService, CoPermissionType} from '@mydocma/co';
import {AuthService} from '@mydocma/auth';
import { PlatformModeStateService } from '../mm-offline/services/platform-mode-state.service';
import { CurrentProjectStateService } from '@docma-mm-core/project/current-project-state.service';

@UntilDestroy()
@Injectable()
export class MmProjectPermissionsService implements OnDestroy {

  private projectPermissionsSubject = new BehaviorSubject<ProjectUserPermissions>(null);

  private poolPermissionsSubject = new BehaviorSubject<any>(null);
  public poolPermissions$ = this.poolPermissionsSubject.asObservable();

  public permissions$ = this.projectPermissionsSubject.asObservable();

  public permissionNewActivity$ = this.hasProjectActionPermissions([ProjectActionPermissionEnum.NewActivity]);
  public permissionViewActivitiesCO$ = this.hasProjectActionPermissions([ProjectActionPermissionEnum.ViewActivitiesCO]);
  public permissionViewActivitiesCT$ = this.hasProjectActionPermissions([ProjectActionPermissionEnum.ViewActivitiesCT]);

  public permissionDefectPoolsVisible$ = this.poolPermissions$.pipe(
    map(poolPerms => !!poolPerms?.['visible']),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public permissionDefectPoolsCreate$ = this.poolPermissions$.pipe(
    map(poolPerms => !!poolPerms?.['create']),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public permissionDefectPoolsUpdate$ = this.poolPermissions$.pipe(
    map(poolPerms => !!poolPerms?.['update']),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public permissionDefectPoolsDelete$ = this.poolPermissions$.pipe(
    map(poolPerms => !!poolPerms?.['delete']),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public permissionDefectPoolsAddDefects$ = this.poolPermissions$.pipe(
    map(poolPerms => !!poolPerms?.['add_defects']),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public permissionDefectPoolsRemoveDefects$ = this.poolPermissions$.pipe(
    map(poolPerms => !!poolPerms?.['remove_defects']),
    distinctUntilChanged(),
    shareReplay(1)
  );

  private costsManagementPermissionsSubject = new BehaviorSubject<any>(null);
  public costsManagementPermissions$ = this.costsManagementPermissionsSubject.asObservable();

  public permissionInvoicesVisible$ = this.costsManagementPermissions$.pipe(
    map(perms => !!perms?.invoices?.visible),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public permissionInvoicesCreate$ = this.costsManagementPermissions$.pipe(
    map(perms => !!perms?.invoices?.create),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public permissionInvoicesUpdate$ = this.costsManagementPermissions$.pipe(
    map(perms => !!perms?.invoices?.update),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public permissionInvoicesDelete$ = this.costsManagementPermissions$.pipe(
    map(perms => !!perms?.invoices?.delete),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public permissionTimeTrackingVisible$ = this.costsManagementPermissions$.pipe(
    map(perms => !!perms?.timeTracking?.visible),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public permissionTimeTrackingCreate$ = this.costsManagementPermissions$.pipe(
    map(perms => !!perms?.timeTracking?.create),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public permissionTimeTrackingUpdate$ = this.costsManagementPermissions$.pipe(
    map(perms => !!perms?.timeTracking?.update),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public permissionTimeTrackingRecalculate$ = this.costsManagementPermissions$.pipe(
    map(perms => !!perms?.timeTracking?.recalculate),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public permissionTimeTrackingDelete$ = this.costsManagementPermissions$.pipe(
    map(perms => !!perms?.timeTracking?.delete),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public permissionCostsVisible$ = combineLatest([
    this.permissionInvoicesVisible$,
    this.permissionTimeTrackingVisible$
  ]).pipe(
    map(([invoices, timeTracking]) => invoices || timeTracking),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public hasShortcuts$ = this.hasShortcuts();

  public shortcuts$ = this.permissions$
    .pipe(
      switchMap(p => this.projectLoaderService.statuses.pipe(
        switchMap(st => st.loadState$.pipe(filter(ls => ls.status == LoadStatusEnum.Loaded)).pipe(
          switchMap(status => this.permissionViewActivitiesCO$.pipe(
            switchMap(permActivitiesCO => this.permissionViewActivitiesCT$.pipe(
              map(permActivitiesCT => p.Shortcuts
                ?.filter(f =>
                  ((f.Status.ViewPoint === ViewPoint.Contractor && permActivitiesCO)
                    || (f.Status.ViewPoint === ViewPoint.Customer && permActivitiesCT))
                  && status.data.findIndex(f2 => f2.Id == f.Status.Id) !== -1
                )
                ?.sort(x => x.Symbol) ?? []
              )
              // , tap(x => {
              //   console.info('' + JSON.stringify(x));
              // })
            ))
          ))
        ))
      ))
      , map(x => x)
    );

  constructor(private readonly projectService: MmProjectService,
              private readonly connectivityService: ConnectivityService,
              private readonly offlineService: MmProjectOfflineService,
              private readonly projectLoaderService: MmProjectLoaderService,
              private readonly coPermissionsService: CoPermissionsService,
              private readonly authService: AuthService,
              private readonly platformModeStateService: PlatformModeStateService,
              private readonly currentProjectState: CurrentProjectStateService) {

    this.projectService.currentProject
      .pipe(
        untilDestroyed(this),
        tap(p => !p && this.projectPermissionsSubject.next(null)),
        switchMap(p => !p ? of(p) :
          this.offlineService.getPermissions(p, false)
            .pipe(
              tap(ps => this.notifyPermissionsChange(p, ps)),
              map(ps => p)
            )
        ),
        switchMap(p =>
          combineLatest([this.connectivityService.appIsInOfflineMode, this.platformModeStateService.isOnline$])
          .pipe(
            filter(([appOffline, platformOnline]) => !appOffline && platformOnline),
            map(() => p)
          )
        ),
        switchMap(p => !p ? of(null) :
          this.offlineService.getPermissions(p, true)
            .pipe(
              tap(ps => this.notifyPermissionsChange(p, ps)),
              map(ps => ps)
            )

        )
      )
      .subscribe();

    this.authService.userPermissions.pipe(untilDestroyed(this)).subscribe((p: any) => {
      const poolPerms = p?.project?.defects?.filters?.defect_pools;
      this.poolPermissionsSubject.next(poolPerms);

      const costsPerms = p?.schema?.mm?.costsManagement;
      this.costsManagementPermissionsSubject.next(costsPerms);
    });

    this.authService.user.subscribe(user => {
      this.currentProjectState.setUserId(user?.id);
    });
  }

  ngOnDestroy(): void {
  }

  private notifyPermissionsChange(p: MmProject, ps: ProjectUserPermissions) {
    if (!ps) {
      ps = {};
    }
    ps.Project = p;

    this.projectPermissionsSubject.next(ps);
  }

  public hasRoutePermissions(permissions: ProjectUserPermissions, route: string): boolean {
     const idToPermissionMap = new Map<string, ConfigPermissionEnum | CoPermissionArea>([
      [MmRoutesEnum.project_informations, ConfigPermissionEnum.ProjectInformation],
      [MmRoutesEnum.project_settings, ConfigPermissionEnum.ProjectConfiguration],
      [MmRoutesEnum.project_companies, ConfigPermissionEnum.ProjectCompany],
      [MmRoutesEnum.project_crafts, ConfigPermissionEnum.ProjectCraft],
      [MmRoutesEnum.project_variables, ConfigPermissionEnum.ProjectVariables],
      [MmRoutesEnum.project_type1, ConfigPermissionEnum.ProjectUserDefined1],
      [MmRoutesEnum.project_type2, ConfigPermissionEnum.ProjectUserDefined2],
      [routeToPath.get(MmRoutesEnum.project_informations), ConfigPermissionEnum.ProjectInformation],
      [routeToPath.get(MmRoutesEnum.project_settings), ConfigPermissionEnum.ProjectConfiguration],
      [routeToPath.get(MmRoutesEnum.project_companies), ConfigPermissionEnum.ProjectCompany],
      [routeToPath.get(MmRoutesEnum.project_crafts), ConfigPermissionEnum.ProjectCraft],
      [routeToPath.get(MmRoutesEnum.project_variables), ConfigPermissionEnum.ProjectVariables],
      [routeToPath.get(MmRoutesEnum.project_type1), ConfigPermissionEnum.ProjectUserDefined1],
      [routeToPath.get(MmRoutesEnum.project_type2), ConfigPermissionEnum.ProjectUserDefined2],
      [MmRoutesEnum.co_structures, CoPermissionArea.Structure],
      [routeToPath.get(MmRoutesEnum.co_bim_models), CoPermissionArea.BimModels],
      [routeToPath.get(MmRoutesEnum.co_structures), CoPermissionArea.Structure],
      [routeToPath.get(MmRoutesEnum.co_bim_sections), CoPermissionArea.BimSections],
      [routeToPath.get(MmRoutesEnum.co_plans), CoPermissionArea.Plans],
    ]);

     const routePermission = idToPermissionMap.get(route);
     if (!routePermission) {
      return true;
    }

     let hasPermission = false;
     if (routePermission == CoPermissionArea.Structure || routePermission == CoPermissionArea.BimModels || routePermission == CoPermissionArea.BimSections || routePermission == CoPermissionArea.Plans) {
      hasPermission = this.coPermissionsService.hasProjectPermission(routePermission as CoPermissionArea, CoPermissionType.Visible);
    } else {
      hasPermission = permissions?.Configs?.findIndex(cp => cp.Config == routePermission &&
        ((cp.Permission & DataAccessPermissionEnum.Read) === DataAccessPermissionEnum.Read || (cp.Permission & DataAccessPermissionEnum.Write) === DataAccessPermissionEnum.Write)) != -1;
    }

     return hasPermission;
  }

  public hasConfigAccessPermission(permissions: ProjectUserPermissions, configPermission: ConfigPermission): boolean {
    if (!permissions || !permissions.Configs) {
      return false;
    }

    const foundPermission = permissions.Configs.find(c => c.Config === configPermission.Config);

    if (!foundPermission || !foundPermission.Permission) {
      return false;
    }

    if ((foundPermission.Permission & configPermission.Permission) === configPermission.Permission) {
        return true;
    }

    return false;
  }

  public hasProjectActionPermission(permissions: ProjectUserPermissions, action: ProjectActionPermissionEnum): boolean {
    if (!permissions || !permissions.Actions) {
      return false;
    }
    const found = permissions.Actions?.find(a => a === action);
    return !!found;
  }

  public hasActionPermission(permissions: ProjectUserPermissions, action: ActionPermissionEnum): boolean {
    if (!permissions || !permissions.Actions) {
      return false;
    }
    const found = permissions.AdminActions?.find(a => a === action);
    return !!found;
  }

  public hasProjectConfigPermissions(permissions: ConfigPermission[]): Observable<boolean> {
    if (!permissions || permissions.length === 0) {
      return of(true);
    }
    return this.projectService.currentProject
      .pipe(
        switchMap(project =>
          this.permissions$
            .pipe(
              filter(ps => !project || (ps && ps.Project && ps.Project.Id === project.Id)),
              first()
            )
        ),
        map(userPermissions => {
          for (const perm of permissions) {
            if (!this.hasConfigAccessPermission(userPermissions, perm)) {
              return false;
            }
          }
          return true;
        })
      );
  }

  public hasProjectActionPermissions(actions: ProjectActionPermissionEnum[]): Observable<boolean> {
    if (!actions || actions.length === 0) {
      return of(true);
    }
    return this.projectService.currentProject
      .pipe(
        switchMap(project =>
          this.permissions$
            .pipe(
              filter(ps => !project || (ps && ps.Project && ps.Project.Id === project.Id)),

            )
        ),
        map(userPermissions => {
          for (const a of actions) {
            if (!this.hasProjectActionPermission(userPermissions, a)) {
              return false;
            }
          }
          return true;
        })
      );
  }

  public hasActionPermissions(actions: ActionPermissionEnum[]): Observable<boolean> {
    if (!actions || actions.length === 0) {
      return of(true);
    }
    return this.projectService.currentProject
      .pipe(
        switchMap(project =>
          this.permissions$
            .pipe(
              filter(ps => !project || (ps && ps.Project && ps.Project.Id === project.Id)),

            )
        ),
        map(userPermissions => {
          for (const a of actions) {
            if (!this.hasActionPermission(userPermissions, a)) {
              return false;
            }
          }
          return true;
        })
      );
  }

  public hasShortcuts(): Observable<boolean> {
    return this.projectService.currentProject
      .pipe(
        switchMap(project =>
          this.permissions$
            .pipe(
              filter(ps => !project || (ps && ps.Project && ps.Project.Id === project.Id)),
            )
        ),
        map(userPermissions => {
          if (!userPermissions) {
            return false;
          }
          return ((userPermissions.Shortcuts?.length ?? 0) > 0);
        })
      );
  }

  public canUpdatePool(pool: any, currentUser: any): Observable<boolean> {
    return this.permissionDefectPoolsCreate$.pipe(
      switchMap(hasCreate => {
        const isOwn = this.isOwnPool(pool, currentUser);
        if (hasCreate && isOwn) {
          return of(true);
        }
        return this.permissionDefectPoolsUpdate$;
      })
    );
  }

  public canDeletePool(pool: any, currentUser: any): Observable<boolean> {
    return this.permissionDefectPoolsCreate$.pipe(
      switchMap(hasCreate => {
        const isOwn = this.isOwnPool(pool, currentUser);
        if (hasCreate && isOwn) {
          return of(true);
        }
        return this.permissionDefectPoolsDelete$;
      })
    );
  }

  public canAddDefectsToPool(pool: any, currentUser: any): Observable<boolean> {
    return this.permissionDefectPoolsCreate$.pipe(
      switchMap(hasCreate => {
        if (hasCreate && this.isOwnPool(pool, currentUser)) {
          return of(true);
        }
        return this.permissionDefectPoolsAddDefects$;
      })
    );
  }

  public canRemoveDefectsFromPool(pool: any, currentUser: any): Observable<boolean> {
    return this.permissionDefectPoolsCreate$.pipe(
      switchMap(hasCreate => {
        if (hasCreate && this.isOwnPool(pool, currentUser)) {
          return of(true);
        }
        return this.permissionDefectPoolsRemoveDefects$;
      })
    );
  }

  private isOwnPool(pool: any, currentUser: any): boolean {
    if (!pool || !currentUser) {
      return false;
    }

    let isOwnByCreator = false;
    if (pool.Creator) {
      isOwnByCreator = pool.Creator === currentUser.Account || pool.Creator === currentUser.Name;
    }

    let isOwnByUserAccount = false;
    if (pool.User?.Account) {
      isOwnByUserAccount = pool.User.Account === currentUser.Account;
    }

    return isOwnByCreator || isOwnByUserAccount;
  }
}
